// Formatters for the Analytics surface.
// All inputs use the API's units (0..1 fractions for rates/scores, seconds for durations).

export const formatRatePercent = (value: number, fractionDigits = 1): string => {
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(fractionDigits)}%`;
};

export const formatInt = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString();
};

export const formatDurationSeconds = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Delta direction semantics — which way is "good" for a metric.
export type DeltaDirection = 'up-is-good' | 'down-is-good' | 'neutral';

export interface DeltaDisplay {
  label: string;
  tone: 'positive' | 'negative' | 'muted';
  arrow: 'up' | 'down' | 'flat';
}

const NO_CHANGE_DELTA: DeltaDisplay = { label: 'No change', tone: 'muted', arrow: 'flat' };

// Percentage-point delta for a rate/score in 0..1.
// e.g. 0.711 vs 0.702 => "+0.9 pp"
export const formatPpDelta = (
  value: number,
  priorValue: number | undefined,
  direction: DeltaDirection = 'up-is-good',
): DeltaDisplay => {
  if (!Number.isFinite(value) || priorValue == null || !Number.isFinite(priorValue)) {
    return NO_CHANGE_DELTA;
  }
  const diffPoints = (value - priorValue) * 100;
  return resolveDelta(diffPoints, diffPoints.toFixed(1), 'pp', direction);
};

// Count delta — e.g. 109 vs 104 => "+5"
export const formatCountDelta = (
  value: number,
  priorValue: number | undefined,
  direction: DeltaDirection = 'up-is-good',
): DeltaDisplay => {
  if (!Number.isFinite(value) || priorValue == null || !Number.isFinite(priorValue)) {
    return NO_CHANGE_DELTA;
  }
  const diff = value - priorValue;
  return resolveDelta(diff, String(Math.round(diff)), undefined, direction);
};

// Duration delta — e.g. 364s vs 314s => "+50s"
export const formatDurationDelta = (
  seconds: number,
  priorSeconds: number | undefined,
  direction: DeltaDirection = 'down-is-good',
): DeltaDisplay => {
  if (!Number.isFinite(seconds) || priorSeconds == null || !Number.isFinite(priorSeconds)) {
    return NO_CHANGE_DELTA;
  }
  const diff = seconds - priorSeconds;
  return resolveDelta(diff, String(Math.round(diff)), 's', direction);
};

const resolveTone = (rawDiff: number, direction: DeltaDirection): DeltaDisplay['tone'] => {
  if (direction === 'up-is-good') return rawDiff > 0 ? 'positive' : 'negative';
  if (direction === 'down-is-good') return rawDiff < 0 ? 'positive' : 'negative';
  return 'muted';
};

const isZeroDiff = (rawDiff: number, magnitudeText: string): boolean =>
  rawDiff === 0 || magnitudeText === '0' || magnitudeText === '0.0';

const resolveDelta = (
  rawDiff: number,
  magnitudeText: string,
  unit: 'pp' | 's' | undefined,
  direction: DeltaDirection,
): DeltaDisplay => {
  if (isZeroDiff(rawDiff, magnitudeText)) {
    return NO_CHANGE_DELTA;
  }
  const sign = rawDiff > 0 ? '+' : '';
  const unitPart = unit ? ` ${unit}` : '';
  return {
    label: `${sign}${magnitudeText}${unitPart}`,
    tone: resolveTone(rawDiff, direction),
    arrow: rawDiff > 0 ? 'up' : 'down',
  };
};
